import React, { useRef, useState, useEffect } from "react";
import { X, Camera, Upload, Check, Loader, CheckCircle } from "lucide-react";

const RedeemGiftModal = ({
  showRedeemModal,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const [signatureCanvas, setSignatureCanvas] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selfieImage, setSelfieImage] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [cameraFacing, setCameraFacing] = useState("user");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successData, setSuccessData] = useState(null);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Redirect to dashboard after success
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        // Use window.location.href for full page navigation
        window.location.href = "/dashboard";
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // Signature Functions
  const startSignature = (e) => {
    if (!canvasRef.current) return;
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const rect = canvasRef.current.getBoundingClientRect();

    let x, y;
    if (e.touches) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const drawSignature = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const rect = canvasRef.current.getBoundingClientRect();

    let x, y;
    if (e.touches) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.lineTo(x, y);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  };

  const endSignature = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      setSignatureCanvas(canvasRef.current.toDataURL());
    }
  };

  const clearSignature = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setSignatureCanvas(null);
    }
  };

  // Selfie Functions
  const startSelfieCamera = async (facingMode = null) => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      setCameraError(null);
      const facing = facingMode || cameraFacing;

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const errorMsg = "Camera access is not available. Please use the upload option instead.";
        setCameraError(errorMsg);
        alert(errorMsg);
        return;
      }

      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
          setIsCameraActive(true);
          setCameraFacing(facing);
        } catch (playError) {
          console.error("Video play error:", playError);
          stream.getTracks().forEach((track) => track.stop());
          throw new Error("Failed to start video playback");
        }
      }
    } catch (err) {
      console.error("Camera access error:", err);
      let errorMessage = "Failed to access camera";

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = "Camera permission denied. Please allow camera access in your browser settings.";
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = "No camera found. Please connect a camera or use the upload option.";
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = "Camera is already in use by another application. Please close other apps using the camera.";
      } else if (err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError') {
        errorMessage = "Camera constraints not satisfied. Trying alternative camera...";
        if (cameraFacing === "user") {
          try {
            await startSelfieCamera("environment");
            return;
          } catch (retryErr) {
            errorMessage = "Could not access any camera. Please use the upload option.";
          }
        } else {
          try {
            await startSelfieCamera("user");
            return;
          } catch (retryErr) {
            errorMessage = "Could not access any camera. Please use the upload option.";
          }
        }
      } else if (err.message) {
        errorMessage = `Failed to access camera: ${err.message}`;
      }

      setCameraError(errorMessage);
      alert(errorMessage);
      setIsCameraActive(false);
    }
  };

  const switchCamera = async () => {
    const newFacing = cameraFacing === "user" ? "environment" : "user";
    await startSelfieCamera(newFacing);
  };

  const captureSelfie = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);
    setSelfieImage(canvas.toDataURL());
    stopSelfieCamera();
  };

  const stopSelfieCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const handleSelfieUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setSelfieImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    try {
      const result = await onSubmit({
        signature: signatureCanvas,
        selfie: selfieImage,
      });

      // If onSubmit completes without throwing, show success
      if (result && result.data) {
        setSuccessData(result.data);
        setSuccessMessage(result.message || "Gift redeemed successfully!");
      } else {
        setSuccessMessage("Gift redeemed successfully!");
      }
      setShowSuccess(true);
    } catch (error) {
      console.error("Submission error:", error);
      alert("Failed to redeem gift. Please try again.");
    }
  };

  const handleClose = () => {
    if (!showSuccess) {
      stopSelfieCamera();
      setSelfieImage(null);
      setCameraError(null);
      clearSignature();
      onClose();
    }
  };

  if (!showRedeemModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[95vh] overflow-y-auto my-4 relative">
        {/* Loading Overlay */}
        {isSubmitting && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 rounded-lg">
            <div className="flex flex-col items-center gap-4">
              <Loader className="w-12 h-12 animate-spin text-amber-600" />
              <p className="text-lg font-semibold text-gray-700">
                Redeeming Gift...
              </p>
              <p className="text-sm text-gray-500">
                Please wait while we process your redemption
              </p>
            </div>
          </div>
        )}

        {/* Success Overlay */}
        {showSuccess && (
          <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50 rounded-lg p-4">
            <div className="flex flex-col items-center gap-4 text-center max-w-md">
              <div className="relative">
                <CheckCircle className="w-20 h-20 text-green-500 animate-bounce" />
              </div>
              <p className="text-2xl font-bold text-gray-800">
                Success!
              </p>
              
              {successData?.gift && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 w-full border-2 border-green-300">
                  <div className="mb-3">
                    {successData.gift.gift_image && (
                      <img 
                        src={successData.gift.gift_image} 
                        alt={successData.gift.gift_name}
                        className="w-32 h-32 object-cover rounded-lg mx-auto mb-3"
                      />
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {successData.gift.gift_name}
                  </h3>
                  <p className="text-sm text-gray-700 mb-2">
                    QR Codes Redeemed: <span className="font-bold text-green-600">{successData.gift.qr_count}</span>
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    Scheme: {successData.gift.qr_scheme}
                  </p>
                  {successData.redemption_id && (
                    <p className="text-xs text-gray-600">
                      Redemption ID: <span className="font-mono font-bold">{successData.redemption_id}</span>
                    </p>
                  )}
                </div>
              )}

              <p className="text-lg text-gray-600">
                {successMessage}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Redirecting to dashboard...
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="sticky top-0 bg-amber-600 p-4 md:p-6 text-white flex justify-between items-center">
          <h2 className="text-xl md:text-2xl font-bold">Gift Redemption</h2>
          <button
            onClick={handleClose}
            className="text-white hover:text-blue-100"
            disabled={showSuccess}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 md:p-6 space-y-6">
          {/* Signature Section */}
          <div className="border-2 border-gray-300 rounded-lg p-4 md:p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Step 1: Signature
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please sign below using your finger or mouse
            </p>

            <div className="bg-gray-50 rounded-lg border-2 border-gray-300 p-2">
              <canvas
                ref={canvasRef}
                width={400}
                height={200}
                onMouseDown={startSignature}
                onMouseMove={drawSignature}
                onMouseUp={endSignature}
                onMouseLeave={endSignature}
                onTouchStart={startSignature}
                onTouchMove={drawSignature}
                onTouchEnd={endSignature}
                className="w-full border-2 border-gray-400 rounded bg-white"
                style={{
                  touchAction: "none",
                  cursor: "crosshair",
                  display: "block",
                  maxHeight: "200px",
                }}
              />
            </div>

            <div className="flex gap-2 mt-3">
              <button
                onClick={clearSignature}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded font-semibold transition text-sm w-full"
              >
                Clear Signature
              </button>
            </div>
          </div>

          {/* Selfie Section */}
          <div className="border-2 border-gray-300 rounded-lg p-4 md:p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Step 2: Selfie/Photo
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Take a selfie or upload a photo
            </p>

            {!selfieImage ? (
              <div className="space-y-3">
                {/* Camera Error Message */}
                {cameraError && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800 p-3 rounded">
                    <p className="text-sm font-medium">{cameraError}</p>
                  </div>
                )}

                {/* Camera View */}
                <div className="relative bg-black rounded-lg overflow-hidden min-h-[256px] flex items-center justify-center">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-64 object-cover"
                  />
                  {!isCameraActive && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 bg-opacity-75">
                      <button
                        onClick={() => startSelfieCamera("user")}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-full font-semibold transition z-10 mb-2 flex items-center gap-2"
                      >
                        <Camera className="w-5 h-5" />
                        Start Front Camera
                      </button>
                      <button
                        onClick={() => startSelfieCamera("environment")}
                        className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-full font-semibold transition z-10 flex items-center gap-2"
                      >
                        <Camera className="w-5 h-5" />
                        Start Back Camera
                      </button>
                    </div>
                  )}
                </div>

                {/* Camera Controls */}
                {isCameraActive && (
                  <div className="flex gap-2">
                    <button
                      onClick={captureSelfie}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2"
                    >
                      <Camera className="w-5 h-5" />
                      Capture Photo
                    </button>
                    <button
                      onClick={switchCamera}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2"
                      title={`Switch to ${cameraFacing === "user" ? "Back" : "Front"} Camera`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Switch
                    </button>
                    <button
                      onClick={stopSelfieCamera}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-3 rounded-lg font-semibold transition"
                      title="Stop Camera"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={selfieImage}
                    alt="Selfie"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <button
                  onClick={() => {
                    setSelfieImage(null);
                    setCameraError(null);
                    stopSelfieCamera();
                    setTimeout(() => {
                      startSelfieCamera();
                    }, 100);
                  }}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-semibold transition text-sm"
                >
                  Retake Photo
                </button>
                <p className="text-xs text-green-600 font-semibold">
                  ✓ Photo captured
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-100 border-t p-4 md:p-6 flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-400 hover:bg-gray-500 text-white py-2 md:py-3 rounded-lg font-semibold transition text-sm md:text-base"
            disabled={showSuccess}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!signatureCanvas || !selfieImage || isSubmitting || showSuccess}
            className={`flex-1 py-2 md:py-3 rounded-lg font-semibold transition text-sm md:text-base flex items-center justify-center gap-2 ${
              signatureCanvas && selfieImage && !isSubmitting && !showSuccess
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isSubmitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Submit & Redeem
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RedeemGiftModal;